import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_WebFonts() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🌐 Advanced CSS: Web Fonts</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        Web fonts allow you to use custom fonts beyond system defaults. With{" "}
        <b>@font-face</b> and services like <b>Google Fonts</b>, you can create
        a unique visual identity. However, choosing the right font strategy is
        important for performance and accessibility.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#075985" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Use <code>@font-face</code> to load custom fonts from local or
            remote sources.
          </li>
          <li>
            Always provide <b>fallback fonts</b> (e.g., Arial, sans-serif).
          </li>
          <li>
            Google Fonts offers easy import via <code>&lt;link&gt;</code> or{" "}
            <code>@import</code>.
          </li>
          <li>
            Use <code>font-display: swap;</code> to avoid blank text during
            load.
          </li>
          <li>Limit font weights/styles to reduce load time.</li>
        </ul>
      </div>

      {/* Google Fonts */}
      <div className="tutorial-section">
        <h2 className="section-title">📥 Importing Google Fonts</h2>
        <p>
          Embed in <code>&lt;head&gt;</code>:
        </p>
        <CodeCopyBlock
          code={`<link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;600&display=swap" rel="stylesheet">`}
        />
        <p>Use in CSS:</p>
        <CodeCopyBlock
          code={`body {
  font-family: 'Poppins', sans-serif;
}`}
        />
        <div
          style={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: "1.5rem",
            marginTop: "1rem",
          }}
        >
          Example with Poppins
        </div>
      </div>

      {/* @font-face */}
      <div className="tutorial-section">
        <h2 className="section-title">⚙️ Using @font-face</h2>
        <CodeCopyBlock
          code={`@font-face {
  font-family: "CustomFont";
  src: url("/fonts/CustomFont.woff2") format("woff2"),
       url("/fonts/CustomFont.woff") format("woff");
  font-weight: normal;
  font-style: normal;
  font-display: swap;
}

h1 {
  font-family: "CustomFont", Arial, sans-serif;
}`}
        />
        <p>
          This lets you host fonts locally. Always provide multiple formats like{" "}
          <code>woff2</code> (modern) and <code>woff</code> (fallback).
        </p>
      </div>

      {/* Fallback Stacks */}
      <div className="tutorial-section">
        <h2 className="section-title">🔄 Fallback Font Stacks</h2>
        <CodeCopyBlock
          code={`p {
  font-family: "Roboto", "Helvetica Neue", Arial, sans-serif;
}`}
        />
        <p>
          If Roboto fails, the browser uses Helvetica Neue → Arial → sans-serif.
        </p>
      </div>

      {/* Variable Fonts */}
      <div className="tutorial-section">
        <h2 className="section-title">🎚️ Variable Fonts</h2>
        <p>
          Modern fonts support <b>variable weights & styles</b> in one file.
        </p>
        <CodeCopyBlock
          code={`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@100..900&display=swap');

.text {
  font-family: "Inter", sans-serif;
  font-weight: 300; /* Light */
}
.text-bold {
  font-weight: 800; /* Extra Bold */
}`}
        />
        <div
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: "1.2rem",
            marginTop: "1rem",
          }}
        >
          <p style={{ fontWeight: 300 }}>Light Weight Text</p>
          <p style={{ fontWeight: 800 }}>Bold Weight Text</p>
        </div>
      </div>

      {/* Best Practices */}
      <div className="tutorial-section">
        <h2 className="section-title">✅ Best Practices</h2>
        <ul className="tutorial-list-points">
          <li>
            Preload critical fonts using <code>&lt;link rel="preload"&gt;</code>
            .
          </li>
          <li>Use system fonts for body text (fast loading).</li>
          <li>Only import needed font weights & styles.</li>
          <li>Test across browsers for font rendering differences.</li>
        </ul>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Experiment with <b>Google Variable Fonts</b> for flexible designs.
          </li>
          <li>Try self-hosting fonts for faster performance.</li>
          <li>
            Learn about <code>font-display</code> strategies (swap, fallback,
            block).
          </li>
          <li>
            Explore <b>system font stacks</b> for a native look & faster
            rendering.
          </li>
          <li>
            Combine fonts smartly (serif for headings, sans-serif for body).
          </li>
        </ul>
      </div>
    </div>
  );
}
